/**
 * 🔴 **One frozen empty array, shared — and it is a fix, not a tidy-up.**
 *
 * `data: query.data ?? []` builds a **new array on every render** while a query
 * is in flight. TanStack Table treats a changed `data` identity as new data,
 * recomputes its row model, and re-renders — which produces another new `[]`,
 * and the tab locks up with Chrome's *Page Unresponsive* dialog.
 *
 * It only fires on a **transition**, because that is the only time `data` is
 * briefly `undefined`: changing the period changes the query key, React Query
 * returns `undefined` while refetching, and the loop starts. Navigating
 * straight to the same page is fine — the data is there on first paint, so the
 * reference never churns. That is why this survived a "fixed" verdict in
 * pass 0 and every browser check since: nothing in the suite changes a filter
 * on a loaded page.
 */
export const NO_ROWS: never[] = []

/**
 * 🔴 **Which columns a table's search box reads — named, never inferred.**
 *
 * TanStack's default `getColumnCanGlobalFilter` searches a column only if its
 * value **in the first row** is a string or number. So when the newest post is
 * unmatched — a null title, episode and author — those three columns silently
 * leave the search, and only Channel is searched. Found 13 Oct in production:
 * Saved Stories' newest row was unmatched once ST-28 stopped using Snap's
 * caption, and searching *"Leaving (XP) 2: TT Snap"* returned nothing. The ids
 * passed here are the ones each table's placeholder promises.
 */
export function searchOnly(ids: readonly string[]): (column: { id: string }) => boolean {
  const searchable = new Set(ids)
  return (column) => searchable.has(column.id)
}
